import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import Modal from "../components/Modal";

const EditClient = () => {
  const { userId } = useParams();
  const [formData, setFormData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const res = await api.get(`/admin/clients/${userId}`);
        // console.log("form data will be:", res.data.data);
        setFormData(res.data.data);
      } catch (err) {
        setError("Failed to load client");
      }
    };
    fetchClient();
  }, [userId]);

  useEffect(() => {
    if (showConfirmModal || isProcessing) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showConfirmModal, isProcessing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const upperCaseFields = ["panNumber", "bankDetails.ifscCode"];
    const capitalizeFields = ["name"];

    const capitalizeName = (name) =>
      name
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");

    const normalize = (fieldName, fieldValue) => {
      if (upperCaseFields.includes(fieldName)) {
        return fieldValue.toUpperCase();
      } else if (capitalizeFields.includes(fieldName)) {
        return capitalizeName(fieldValue);
      } else {
        return fieldValue;
      }
    };

    if (name.startsWith("bankDetails.")) {
      const field = name.split(".")[1];
      const fieldPath = `bankDetails.${field}`;
      setFormData({
        ...formData,
        bankDetails: {
          ...formData.bankDetails,
          [field]: normalize(fieldPath, value),
        },
      });
    } else {
      setFormData({ ...formData, [name]: normalize(name, value) });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const handleConfirmUpdate = async () => {
    setIsProcessing(true);
    setShowConfirmModal(false);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append(
        "dob",
        formData.dob ? new Date(formData.dob).toISOString().split("T")[0] : ""
      );
      formDataToSend.append("panNumber", formData.panNumber);
      formDataToSend.append("password", formData.password);
      formDataToSend.append(
        "bankDetails",
        JSON.stringify({
          bankName: formData.bankDetails.bankName,
          accountNumber: formData.bankDetails.accountNumber,
          ifscCode: formData.bankDetails.ifscCode || "",
        })
      );
      if (selectedFile) {
        formDataToSend.append("profilePicture", selectedFile);
        // console.log("Adding file to request:", selectedFile.name);
      }
      await api.put(`/admin/clients/${userId}`, formDataToSend);
      navigate("/admin/dashboard");
    } catch (err) {
      console.error("Update error:", err);
      setError(err.response?.data?.message || "Failed to update client");
      setIsProcessing(false);
    }
  };

  const handleCancelUpdate = () => {
    navigate(-1);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // console.log("File selected:", file.name);
      setSelectedFile(file);
    }
  };

  if (!formData) return <div>Loading...</div>;

  return (
    <>
      <div className="card main-content max-w-xl relative">
        <h2 className="text-xl font-bold mb-4">Edit Client: {formData.name}</h2>
        {error && <div className="alert alert-danger mb-4">{error}</div>}
        <form onSubmit={handleFormSubmit}>
          {/* Personal Details */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Personal Details</h3>
            <div className="input-row">
              <div className="form-group col-span-2">
                <div className="flex items-center gap-2">
                  <img
                    src={
                      selectedFile
                        ? URL.createObjectURL(selectedFile)
                        : formData.profilePicture
                        ? formData.profilePicture.startsWith("http://") ||
                          formData.profilePicture.startsWith("https://")
                          ? formData.profilePicture
                          : process.env.REACT_APP_API_URL +
                            formData.profilePicture
                        : "/path/to/placeholder-image.jpg"
                    }
                    alt="Profile"
                    className="profile-pic"
                  />
                  <label
                    htmlFor="updateProfilePicture"
                    className="btn btn-outline btn-sm"
                  >
                    Upload New Image
                  </label>
                  <input
                    name="profilePicture"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="updateProfilePicture"
                    disabled={isProcessing}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="dob">Date of Birth</label>
                <input
                  id="dob"
                  name="dob"
                  type="date"
                  value={
                    formData.dob
                      ? new Date(formData.dob).toISOString().split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="panNumber">PAN Number</label>
                <input
                  id="panNumber"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Bank Details</h3>
            <div className="input-row">
              <div className="form-group">
                <label htmlFor="bankName">Bank Name</label>
                <input
                  id="bankName"
                  name="bankDetails.bankName"
                  value={formData.bankDetails.bankName}
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="accountNumber">Account Number</label>
                <input
                  id="accountNumber"
                  name="bankDetails.accountNumber"
                  value={formData.bankDetails.accountNumber}
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="form-group">
                <label htmlFor="ifscCode">IFSC Code</label>
                <input
                  id="ifscCode"
                  name="bankDetails.ifscCode"
                  value={formData.bankDetails.ifscCode}
                  onChange={handleChange}
                  className="form-control"
                  required
                  disabled={isProcessing}
                />
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Security</h3>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="form-control"
                required
                disabled={isProcessing}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 mt-4">
            <button
              type="button"
              onClick={() => navigate("/admin/dashboard")}
              className="btn btn-outline btn-sm"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isProcessing}
            >
              Update Client
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={handleCancelUpdate}
        title="Confirm Update"
        buttons={[
          {
            text: "No",
            onClick: handleCancelUpdate,
            color: "outline",
            disabled: isProcessing,
          },
          {
            text: "Yes, Update",
            onClick: handleConfirmUpdate,
            color: "success",
            disabled: isProcessing,
          },
        ]}
      >
        <p>Are you sure you want to update this client's information?</p>
      </Modal>

      {/* Processing Modal */}
      <Modal
        isOpen={isProcessing}
        showSpinner={true}
        spinnerText="Updating Client Information"
        spinnerSubText="Please do not close this window or your device. You will be redirected once the update is complete."
        size="md"
      />
    </>
  );
};

export default EditClient;
